// src/auth/SupabaseAuthProvider.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Create auth context
const AuthContext = createContext();

// Provider component
export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for session on mount
  useEffect(() => {
    const getSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error.message);
        }

        setUser(session?.user || null);
        setIsAuthenticated(!!session?.user);
        setIsLoading(false);
      } catch (error) {
        console.error('Session check error:', error);
        setIsLoading(false);
      }
    };

    getSession();

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      setIsAuthenticated(!!session?.user);
      setIsLoading(false);
    });

    // Cleanup listener on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Login with email/password
  const login = async (email, password) => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Signup with email/password
  const signup = async (formData) => {
    setIsLoading(true);

    try {
      console.log('Signing up with data:', {
        email: formData.email,
        // Don't log password
        userData: {
          company_name: formData.companyName,
          owner_name: formData.ownerName,
          phone: formData.phone,
          plan: formData.plan,
        },
      });

      // Register user with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            company_name: formData.companyName,
            owner_name: formData.ownerName,
            phone: formData.phone,
            plan: formData.plan,
          },
        },
      });

      if (authError) {
        console.error('Auth signup error:', authError);
        return { success: false, error: authError.message };
      }

      console.log('Auth signup successful:', authData);

      // Store additional user data in a profiles table
      // Only do this if the profiles table exists - if not, just skip this step
      try {
        const { error: profileError } = await supabase
          .from('business_profiles')
          .insert([
            {
              user_id: authData.user.id,
              company_name: formData.companyName,
              owner_name: formData.ownerName,
              email: formData.email,
              phone: formData.phone,
              plan: formData.plan,
              created_at: new Date(),
            },
          ]);

        if (profileError) {
          console.error('Profile creation error:', profileError);
          // Don't fail the signup if profile creation fails
          // This is likely because the table doesn't exist yet
        }
      } catch (profileError) {
        console.error('Profile creation exception:', profileError);
        // Don't fail the signup if profile creation fails
      }

      return { success: true, data: authData };
    } catch (error) {
      console.error('Signup exception:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  };

  // Social login with Google
  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/app/dashboard`,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  };

  // Social login with Facebook
  const signInWithFacebook = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/app/dashboard`,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  };

  // Context value
  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout,
    resetPassword,
    updatePassword,
    signInWithGoogle,
    signInWithFacebook,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within a SupabaseAuthProvider');
  }

  return context;
};
